import * as React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import useSWR from 'swr'
import { fetchTenants, TenantAuthError, type Tenant } from '@/lib/bff/tenants'

/*
 * Loads the tenants the signed-in user is authorised for. The Auth0 access
 * token is fetched on demand and forwarded to the BFF. The request only runs
 * once Auth0 reports an authenticated session; retries are manual (via the UI's
 * retry control) so a failed resolution surfaces an error state instead of
 * silently hammering the endpoint.
 *
 * `refresh` always asks the BFF again (never the cache) and rejects on
 * failure; a tenant switch uses it to re-check the target is still allowed.
 */
export function useTenants() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()

  const load = React.useCallback(async () => {
    const token = await getAccessTokenSilently()
    if (!token) throw new TenantAuthError('No access token available')
    return fetchTenants(token)
  }, [getAccessTokenSilently])

  const { data, error, isLoading, mutate } = useSWR<Tenant[]>(
    isAuthenticated ? 'bff/tenants' : null,
    load,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  )

  const refresh = React.useCallback(async () => {
    const fresh = await load()
    await mutate(fresh, { revalidate: false })
    return fresh
  }, [load, mutate])

  return {
    tenants: data,
    error,
    isLoading,
    retry: () => mutate(),
    refresh,
  }
}
